import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Progress,
  Radio,
  Rate,
  Select,
  Spin,
} from "antd";
import { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import surveyApi, {
  PublicSurvey,
  QuestionType,
  SubmitAnswerRequest,
  MultilingualText,
  SupportedLanguageCode,
  SurveySection,
  SurveyQuestion,
} from "../../survey/api";
import { LANGUAGES } from "../../shared/utils/common";
import { useBranding } from "../../shared/layouts/PublicLayout";
import dayjs from "dayjs";
import clsx from "clsx";
import { CheckCircleOutlined, EditOutlined } from "@ant-design/icons";
import {
  TurnstileTokenError,
  TurnstileWidget,
  TurnstileWidgetHandle,
} from "../components/TurnstileWidget";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
// Must match TURNSTILE_SURVEY_SUBMIT_ACTION in survey-core
const TURNSTILE_ACTION = "survey-submit";

// Default branding images
const DEFAULT_LOGO_IMAGE = "/c1-logo-white.svg";
const DEFAULT_BACKGROUND_IMAGE = "/c1-fitness-bg.webp";

const getLocalizedText = (
  text: MultilingualText | undefined | null,
  selectedLanguage: string,
  fallback: string = ""
): string => {
  if (!text) return fallback;
  const lang = selectedLanguage as SupportedLanguageCode;
  return (
    text[lang] ||
    text.en ||
    text.fr ||
    text.ko ||
    text.vi ||
    text.zh ||
    text.th ||
    text.es ||
    text.ru ||
    text.hi ||
    fallback
  );
};

// Check if a question should be visible based on conditional logic and current form values
const isQuestionVisible = (
  question: SurveyQuestion,
  formValues: Record<string, any>
): boolean => {
  // If no condition is set, the question is always visible
  if (!question.conditionQuestionId || !question.conditionOptionIds?.length) {
    return true;
  }

  // Get the value of the condition question
  const conditionFieldName = `question_${question.conditionQuestionId}`;
  const conditionValue = formValues[conditionFieldName];

  if (conditionValue === undefined || conditionValue === null) {
    // Condition question hasn't been answered yet, hide this question
    return false;
  }

  // Check if any of the condition options are selected
  // For single select (MULTIPLE_CHOICE) - value is a single number
  if (typeof conditionValue === "number") {
    return question.conditionOptionIds.includes(conditionValue);
  }

  // For multi select (MULTI_SELECT) - value is an array of numbers
  if (Array.isArray(conditionValue)) {
    return question.conditionOptionIds.some((optionId) =>
      conditionValue.includes(optionId)
    );
  }

  return false;
};

// Cookie utilities for tracking submitted surveys
const COOKIE_PREFIX = "survey_submitted_";
const LANGUAGE_COOKIE_PREFIX = "survey_language_";
const COOKIE_EXPIRY_DAYS = 365;

// Generate a unique cookie key based on channel and publicId
const getCookieKey = (publicId: string, channel?: string): string => {
  return channel ? `${channel}_${publicId}` : publicId;
};

const setSurveySubmittedCookie = (publicId: string, channel?: string) => {
  const date = new Date();
  date.setTime(date.getTime() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
  const key = getCookieKey(publicId, channel);
  document.cookie = `${COOKIE_PREFIX}${key}=true; expires=${date.toUTCString()}; path=/; SameSite=Strict`;
};

const setSurveyLanguageCookie = (
  publicId: string,
  language: string,
  channel?: string
) => {
  const date = new Date();
  date.setTime(date.getTime() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
  const key = getCookieKey(publicId, channel);
  document.cookie = `${LANGUAGE_COOKIE_PREFIX}${key}=${language}; expires=${date.toUTCString()}; path=/; SameSite=Strict`;
};

const hasSurveyBeenSubmitted = (
  publicId: string,
  channel?: string
): boolean => {
  const key = getCookieKey(publicId, channel);
  return document.cookie
    .split("; ")
    .some((cookie) => cookie.startsWith(`${COOKIE_PREFIX}${key}=`));
};

const getSurveyLanguage = (
  publicId: string,
  channel?: string
): string | null => {
  const key = getCookieKey(publicId, channel);
  const cookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${LANGUAGE_COOKIE_PREFIX}${key}=`));
  return cookie ? cookie.split("=")[1] : null;
};

// ==================== PROGRESS STORAGE UTILITIES ====================

const PROGRESS_STORAGE_PREFIX = "survey_progress_";

interface SurveyProgress {
  formValues: Record<string, any>;
  step: number;
  currentSectionIndex: number;
  selectedLanguage: string;
  savedAt: number; // Unix timestamp
}

// Generate a unique storage key based on channel and publicId
const getProgressStorageKey = (publicId: string, channel?: string): string => {
  const surveyKey = channel ? `${channel}_${publicId}` : publicId;
  return `${PROGRESS_STORAGE_PREFIX}${surveyKey}`;
};

// Save survey progress to localStorage
const saveSurveyProgress = (
  publicId: string,
  progress: SurveyProgress,
  channel?: string
): void => {
  try {
    const key = getProgressStorageKey(publicId, channel);
    localStorage.setItem(key, JSON.stringify(progress));
  } catch (error) {
    console.warn("Failed to save survey progress:", error);
  }
};

// Load survey progress from localStorage
const loadSurveyProgress = (
  publicId: string,
  channel?: string
): SurveyProgress | null => {
  try {
    const key = getProgressStorageKey(publicId, channel);
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const progress = JSON.parse(stored) as SurveyProgress;

    // Check if progress is older than 7 days (auto-expire)
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    if (progress.savedAt < sevenDaysAgo) {
      clearSurveyProgress(publicId, channel);
      return null;
    }

    return progress;
  } catch (error) {
    console.warn("Failed to load survey progress:", error);
    return null;
  }
};

// Clear survey progress from localStorage
const clearSurveyProgress = (publicId: string, channel?: string): void => {
  try {
    const key = getProgressStorageKey(publicId, channel);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("Failed to clear survey progress:", error);
  }
};

// Custom Rating Input with value display
interface RatingInputProps {
  value?: number;
  onChange?: (value: number) => void;
}

const RatingInput: React.FC<RatingInputProps> = ({ value, onChange }) => {
  const [hoverValue, setHoverValue] = useState<number>(0);
  const displayValue = hoverValue || value || 0;

  return (
    <div className="flex items-center gap-3">
      <Rate
        count={10}
        value={value}
        onChange={onChange}
        onHoverChange={setHoverValue}
        className="text-2xl md:text-3xl survey-rating"
      />
      <span className="text-white text-lg font-medium min-w-[50px]">
        {displayValue > 0 ? `(${displayValue}/10)` : ""}
      </span>
    </div>
  );
};

// ==================== REVIEW ANSWERS MODAL ====================

interface ReviewAnswersModalProps {
  open: boolean;
  onClose: () => void;
  onNavigateToQuestion: (sectionIndex: number, questionId: number) => void;
  survey: PublicSurvey;
  formValues: Record<string, any>;
  selectedLanguage: string;
  t: (key: string) => string;
}

const ReviewAnswersModal: React.FC<ReviewAnswersModalProps> = ({
  open,
  onClose,
  onNavigateToQuestion,
  survey,
  formValues,
  selectedLanguage,
  t,
}) => {
  const getSortedSections = (): SurveySection[] => {
    if (!survey?.sections) return [];
    return [...survey.sections].sort((a, b) => a.order - b.order);
  };

  const getLocalizedTextInternal = (
    text: MultilingualText | undefined | null,
    fallback: string = ""
  ): string => {
    if (!text) return fallback;
    const lang = selectedLanguage as SupportedLanguageCode;
    return (
      text[lang] ||
      text.en ||
      text.fr ||
      text.ko ||
      text.vi ||
      text.zh ||
      text.th ||
      text.es ||
      text.ru ||
      text.hi ||
      fallback
    );
  };

  // Get display value for a question's answer
  const getAnswerDisplay = (question: SurveyQuestion): React.ReactNode => {
    const fieldName = `question_${question.id}`;

    switch (question.type) {
      case QuestionType.SHORT_TEXT: {
        // Handle multiple answer boxes
        if (question.answerCount && question.answerCount > 1) {
          const answers: string[] = [];
          for (let i = 0; i < question.answerCount; i++) {
            const value = formValues[`question_${question.id}_${i}`];
            if (value) answers.push(value);
          }
          if (answers.length === 0)
            return (
              <span className="text-gray-500 italic">
                {t("review_not_answered")}
              </span>
            );
          return (
            <div className="space-y-1">
              {answers.map((answer, idx) => (
                <div key={idx} className="text-gray-300">
                  • {answer}
                </div>
              ))}
            </div>
          );
        }
        const value = formValues[fieldName];
        if (!value)
          return (
            <span className="text-gray-500 italic">
              {t("review_not_answered")}
            </span>
          );
        return <span className="text-gray-300">{value}</span>;
      }

      case QuestionType.LONG_TEXT: {
        const value = formValues[fieldName];
        if (!value)
          return (
            <span className="text-gray-500 italic">
              {t("review_not_answered")}
            </span>
          );
        return (
          <span className="text-gray-300 whitespace-pre-wrap">{value}</span>
        );
      }

      case QuestionType.DATE: {
        const value = formValues[fieldName];
        if (!value)
          return (
            <span className="text-gray-500 italic">
              {t("review_not_answered")}
            </span>
          );
        return (
          <span className="text-gray-300">
            {dayjs(value).format("YYYY-MM-DD")}
          </span>
        );
      }

      case QuestionType.MULTIPLE_CHOICE: {
        const selectedId = formValues[fieldName];
        if (!selectedId)
          return (
            <span className="text-gray-500 italic">
              {t("review_not_answered")}
            </span>
          );
        const option = question.options.find((o) => o.id === selectedId);
        if (!option)
          return (
            <span className="text-gray-500 italic">
              {t("review_not_answered")}
            </span>
          );

        const optionText = getLocalizedTextInternal(option.text);
        if (option.isOtherOption) {
          const otherText = formValues[`${fieldName}_other`] || "";
          return (
            <span className="text-gray-300">
              {optionText}: {otherText}
            </span>
          );
        }
        return <span className="text-gray-300">{optionText}</span>;
      }

      case QuestionType.MULTI_SELECT: {
        const selectedIds: number[] = formValues[fieldName] || [];
        if (selectedIds.length === 0)
          return (
            <span className="text-gray-500 italic">
              {t("review_not_answered")}
            </span>
          );

        return (
          <div className="space-y-1">
            {selectedIds.map((id) => {
              const option = question.options.find((o) => o.id === id);
              if (!option) return null;
              const optionText = getLocalizedTextInternal(option.text);
              if (option.isOtherOption) {
                const otherText = formValues[`${fieldName}_other`] || "";
                return (
                  <div key={id} className="text-gray-300">
                    • {optionText}: {otherText}
                  </div>
                );
              }
              return (
                <div key={id} className="text-gray-300">
                  • {optionText}
                </div>
              );
            })}
          </div>
        );
      }

      case QuestionType.RATING: {
        const value = formValues[fieldName];
        if (!value)
          return (
            <span className="text-gray-500 italic">
              {t("review_not_answered")}
            </span>
          );
        return (
          <div className="flex items-center gap-2">
            <Rate
              count={10}
              value={value}
              disabled
              className="text-lg survey-rating"
            />
            <span className="text-gray-300">({value}/10)</span>
          </div>
        );
      }

      default:
        return (
          <span className="text-gray-500 italic">
            {t("review_not_answered")}
          </span>
        );
    }
  };

  // Check if question has an answer
  // const hasAnswer = (question: SurveyQuestion): boolean => {
  //   const fieldName = `question_${question.id}`;

  //   if (
  //     question.type === QuestionType.SHORT_TEXT &&
  //     question.answerCount &&
  //     question.answerCount > 1
  //   ) {
  //     for (let i = 0; i < question.answerCount; i++) {
  //       if (formValues[`question_${question.id}_${i}`]) return true;
  //     }
  //     return false;
  //   }

  //   const value = formValues[fieldName];
  //   if (Array.isArray(value)) return value.length > 0;
  //   return value !== undefined && value !== null && value !== "";
  // };

  const sections = getSortedSections();
  let questionNumber = 1;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 pb-3.5 text-white">
          <CheckCircleOutlined className="text-green-500" />
          {t("review_answers_title")}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button className="mt-5" key="close" type="primary" onClick={onClose}>
          {t("review_close")}
        </Button>,
      ]}
      width={700}
      className="review-answers-modal"
      styles={{
        content: { backgroundColor: "#1b1c31", border: "1px solid #3a3b55" },
        header: {
          backgroundColor: "#1b1c31",
          borderBottom: "1px solid #3a3b55",
        },
        body: { maxHeight: "60vh", overflowY: "auto", padding: "16px" },
        footer: { backgroundColor: "#1b1c31", borderTop: "1px solid #3a3b55" },
      }}
    >
      <div className="space-y-6">
        {sections.map((section, sectionIndex) => {
          const sortedQuestions = [...section.questions].sort(
            (a, b) => a.order - b.order
          );
          // Filter to only visible questions
          const visibleQuestions = sortedQuestions.filter((q) =>
            isQuestionVisible(q, formValues)
          );

          if (visibleQuestions.length === 0) return null;

          return (
            <div
              key={section.id}
              className="border border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Section Header */}
              {getLocalizedTextInternal(section.title) && (
                <div className="bg-[#2a2b45] px-4 py-2 border-b border-gray-700">
                  <h3 className="text-white font-medium">
                    {getLocalizedTextInternal(section.title)}
                  </h3>
                </div>
              )}

              {/* Questions */}
              <div className="divide-y divide-gray-700">
                {visibleQuestions.map((question) => {
                  // Check if conditional - don't number conditional questions
                  const isConditional = !!(
                    question.conditionQuestionId &&
                    question.conditionOptionIds?.length
                  );
                  const currentNumber = isConditional ? null : questionNumber++;
                  // const answered = hasAnswer(question);

                  return (
                    <div
                      key={question.id}
                      className="p-4 hover:bg-[#141421] cursor-pointer transition-colors group"
                      onClick={() => {
                        onNavigateToQuestion(sectionIndex, question.id!);
                        onClose();
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {currentNumber !== null && (
                              <span className="text-gray-500 text-sm">
                                {currentNumber}.
                              </span>
                            )}
                            <span className="text-white font-medium">
                              {getLocalizedTextInternal(question.title)}
                            </span>
                            {question.isRequired && (
                              <span className="text-red-500">*</span>
                            )}
                          </div>
                          <div className="pl-0 md:pl-5">
                            {getAnswerDisplay(question)}
                          </div>
                        </div>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          className="text-gray-400 group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateToQuestion(sectionIndex, question.id!);
                            onClose();
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export const PublicSurveyPage = () => {
  const { publicId, channel } = useParams();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const [form] = Form.useForm();
  const { setBackgroundImageUrl, setLogoImageUrl, logoImageUrl } =
    useBranding();

  // Check if test mode is enabled via ?test=1 query string
  const isTestMode = searchParams.get("internal_test") === "1";

  const [survey, setSurvey] = useState<PublicSurvey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0); // 0 = language selection, 1+ = sections
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [progressRestored, setProgressRestored] = useState(false);

  // Refs for question elements (for scrolling)
  const questionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Ref for pending scroll target
  const pendingScrollTarget = useRef<number | null>(null);
  // Ref to track if initial progress restoration has been attempted
  const initialRestoreAttempted = useRef(false);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  // Check cookie synchronously to prevent race condition with loadSurvey
  // Bypass cookie check if test mode is enabled
  const [alreadySubmitted] = useState(() =>
    publicId && !isTestMode ? hasSurveyBeenSubmitted(publicId, channel) : false
  );

  const loadSurvey = useCallback(
    async (id: string, surveyChannel?: string) => {
      try {
        setLoading(true);
        setError(null);
        // Use channel-based API if channel is provided, otherwise use legacy API
        const response = surveyChannel
          ? await surveyApi.getPublicSurveyByChannel(
              surveyChannel,
              id,
              isTestMode
            )
          : await surveyApi.getPublicSurvey(id, isTestMode);
        const surveyData = response.data ?? null;
        setSurvey(surveyData);

        // Language detection priority:
        // 1. URL query parameter (e.g., ?language=zh)
        // 2. Browser language detection
        // 3. Fallback to first supported language
        const queryLanguage = searchParams.get("language");
        const browserLanguage = navigator.language?.split("-")[0]; // Get primary language code (e.g., "en" from "en-US")

        if (surveyData?.supportedLanguages) {
          let defaultLang: string;

          // First priority: URL query parameter
          if (
            queryLanguage &&
            surveyData.supportedLanguages.includes(queryLanguage)
          ) {
            defaultLang = queryLanguage;
          }
          // Second priority: Browser language
          else if (
            browserLanguage &&
            surveyData.supportedLanguages.includes(browserLanguage)
          ) {
            defaultLang = browserLanguage;
          }
          // Fallback: first supported language
          else {
            defaultLang = surveyData.supportedLanguages[0];
          }

          setSelectedLanguage(defaultLang);
          form.setFieldValue("selectedLanguage", defaultLang);
        } else {
          // No supported languages specified, use query param or browser language or default to "en"
          const defaultLang = queryLanguage || browserLanguage || "en";
          setSelectedLanguage(defaultLang);
          form.setFieldValue("selectedLanguage", defaultLang);
        }
      } catch (err: any) {
        console.error("Error loading survey:", err);
        setError(
          err?.response?.data?.message ||
            "Survey not found or is no longer available"
        );
      } finally {
        setLoading(false);
      }
    },
    [form, searchParams, isTestMode]
  );

  // Restore language if survey was already submitted
  useEffect(() => {
    const restoreLanguage = async () => {
      if (publicId && alreadySubmitted) {
        // Restore the language that was used when submitting
        const savedLanguage = getSurveyLanguage(publicId, channel);
        if (savedLanguage) {
          setSelectedLanguage(savedLanguage);
          await i18n.changeLanguage(savedLanguage);
        }
      }
    };
    restoreLanguage();
  }, [publicId, channel, alreadySubmitted, i18n]);

  // Always load survey (needed for branding even if already submitted)
  useEffect(() => {
    if (publicId) {
      loadSurvey(publicId, channel);
    }
  }, [publicId, channel, loadSurvey]);

  // Restore progress from localStorage after survey loads (only once)
  useEffect(() => {
    if (
      !survey ||
      !publicId ||
      alreadySubmitted ||
      initialRestoreAttempted.current
    )
      return;

    initialRestoreAttempted.current = true;

    const savedProgress = loadSurveyProgress(publicId, channel);
    if (savedProgress) {
      // Restore form values
      form.setFieldsValue(savedProgress.formValues);

      // Restore navigation state
      setStep(savedProgress.step);
      setCurrentSectionIndex(savedProgress.currentSectionIndex);

      // Restore language
      if (savedProgress.selectedLanguage) {
        setSelectedLanguage(savedProgress.selectedLanguage);
        form.setFieldValue("selectedLanguage", savedProgress.selectedLanguage);
      }

      setProgressRestored(true);

      // Clear the restored flag after a short delay
      setTimeout(() => {
        setProgressRestored(false);
      }, 3000);
    }
  }, [survey, publicId, channel, alreadySubmitted, form]);

  // Save progress to localStorage when form values change
  const saveProgress = useCallback(() => {
    if (!publicId || alreadySubmitted || !survey) return;

    const formValues = form.getFieldsValue(true);
    const progress: SurveyProgress = {
      formValues,
      step,
      currentSectionIndex,
      selectedLanguage,
      savedAt: Date.now(),
    };

    saveSurveyProgress(publicId, progress, channel);
  }, [
    publicId,
    channel,
    alreadySubmitted,
    survey,
    form,
    step,
    currentSectionIndex,
    selectedLanguage,
  ]);

  // Auto-save progress when step or section changes
  useEffect(() => {
    if (step > 0 && survey && !alreadySubmitted) {
      saveProgress();
    }
  }, [step, currentSectionIndex, saveProgress, survey, alreadySubmitted]);

  // Handle navigation from review modal to a specific question
  const handleNavigateToQuestion = useCallback(
    (sectionIndex: number, questionId: number) => {
      setCurrentSectionIndex(sectionIndex);
      setStep(1);
      // Store the question ID to scroll to after render
      pendingScrollTarget.current = questionId;
    },
    []
  );

  // Scroll to pending question after section change
  useEffect(() => {
    if (pendingScrollTarget.current !== null) {
      const questionId = pendingScrollTarget.current;
      pendingScrollTarget.current = null;

      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        const element = questionRefs.current.get(questionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          // Add a brief highlight effect
          element.classList.add("highlight-question");
          setTimeout(() => {
            element.classList.remove("highlight-question");
          }, 2000);
        }
      }, 100);
    }
  }, [currentSectionIndex]);

  // Update branding images when survey is loaded
  useEffect(() => {
    if (survey) {
      // Set custom branding or fall back to defaults
      setBackgroundImageUrl(
        survey.backgroundImageUrl || DEFAULT_BACKGROUND_IMAGE
      );
      setLogoImageUrl(survey.logoImageUrl || DEFAULT_LOGO_IMAGE);
    }

    // Reset to defaults when unmounting
    return () => {
      setBackgroundImageUrl(DEFAULT_BACKGROUND_IMAGE);
      setLogoImageUrl(DEFAULT_LOGO_IMAGE);
    };
  }, [survey, setBackgroundImageUrl, setLogoImageUrl]);

  // Update document title when survey is loaded
  useEffect(() => {
    if (survey) {
      const surveyTitle = getLocalizedText(
        survey.title,
        selectedLanguage || "en",
        "Survey"
      );
      document.title = `${surveyTitle}`;
    }
    return () => {
      document.title = "C1 Survey";
    };
  }, [survey, selectedLanguage]);

  useEffect(() => {
    if (selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [selectedLanguage, i18n]);

  const handleLanguageChange = (value: string) => {
    setSelectedLanguage(value);
    form.setFieldValue("selectedLanguage", value);
  };

  // Get sorted sections for navigation
  const getSortedSections = (): SurveySection[] => {
    if (!survey?.sections) return [];
    return [...survey.sections].sort((a, b) => a.order - b.order);
  };

  // Check if a question is conditional (depends on another question's answer)
  const isConditionalQuestion = (question: SurveyQuestion): boolean => {
    return !!(
      question.conditionQuestionId && question.conditionOptionIds?.length
    );
  };

  // Calculate the starting question number for a given section index
  // Only counts non-conditional questions
  const getQuestionStartNumber = (sectionIndex: number): number => {
    const sections = getSortedSections();
    let startNumber = 1;
    for (let i = 0; i < sectionIndex; i++) {
      // Only count non-conditional questions
      const nonConditionalCount =
        sections[i]?.questions?.filter((q) => !isConditionalQuestion(q))
          .length || 0;
      startNumber += nonConditionalCount;
    }
    return startNumber;
  };

  // Validate all required questions in the current section are answered
  // Only validates questions that are visible (respecting conditional logic)
  const validateCurrentSection = async (): Promise<boolean> => {
    const sections = getSortedSections();
    const currentSection = sections[currentSectionIndex];

    if (!currentSection) return true;

    const allValues = form.getFieldsValue(true);
    const fieldNames: string[] = [];

    currentSection.questions
      .filter((q) => {
        // Only validate if (required or selection-limited) AND visible
        const hasSelectionLimit =
          q.type === QuestionType.MULTI_SELECT && !!q.maxSelections;
        if (!q.isRequired && !hasSelectionLimit) return false;
        return isQuestionVisible(q, allValues);
      })
      .forEach((q) => {
        // Handle multiple answer boxes for short text questions
        if (
          q.type === QuestionType.SHORT_TEXT &&
          q.answerCount &&
          q.answerCount > 1
        ) {
          for (let i = 0; i < q.answerCount; i++) {
            fieldNames.push(`question_${q.id}_${i}`);
          }
        } else {
          fieldNames.push(`question_${q.id}`);
        }

        // Also validate "Other" text field if an "Other" option is selected
        if (
          q.type === QuestionType.MULTIPLE_CHOICE ||
          q.type === QuestionType.MULTI_SELECT
        ) {
          const otherOption = q.options.find((o) => o.isOtherOption);
          if (otherOption) {
            const value = allValues[`question_${q.id}`];
            const isOtherSelected =
              q.type === QuestionType.MULTIPLE_CHOICE
                ? value === otherOption.id
                : Array.isArray(value) && value.includes(otherOption.id!);
            if (isOtherSelected) {
              fieldNames.push(`question_${q.id}_other`);
            }
          }
        }
      });

    try {
      await form.validateFields(fieldNames);
      return true;
    } catch {
      return false;
    }
  };

  // Clear validation errors when section changes (after the new form items mount)
  useEffect(() => {
    if (step === 1 && survey?.sections) {
      const sortedSections = [...survey.sections].sort(
        (a, b) => a.order - b.order
      );
      const currentSection = sortedSections[currentSectionIndex];
      if (currentSection) {
        // Use setTimeout to ensure form items have mounted before clearing errors
        const timeoutId = setTimeout(() => {
          const currentSectionFields = currentSection.questions.map(
            (q) => `question_${q.id}`
          );
          form.setFields(
            currentSectionFields.map((name) => ({ name, errors: [] }))
          );
        }, 0);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [step, currentSectionIndex, survey, form]);

  const handleNext = async (e?: React.MouseEvent) => {
    // Prevent any form submission or event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (step === 0) {
      // Moving from language selection to first section
      setStep(1);
      setCurrentSectionIndex(0);
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Save progress when starting survey
      setTimeout(saveProgress, 100);
    } else {
      // Validate current section before moving to next
      const isValid = await validateCurrentSection();
      if (!isValid) {
        return;
      }

      const sections = getSortedSections();
      if (currentSectionIndex < sections.length - 1) {
        // Move to next section
        setCurrentSectionIndex(currentSectionIndex + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
        // Save progress when navigating between sections
        setTimeout(saveProgress, 100);
      }
      // If at last section, the submit button will be shown instead of next
    }
  };

  const handleBack = (e?: React.MouseEvent) => {
    // Prevent any form submission or event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setStep(0);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isLastSection = (): boolean => {
    const sections = getSortedSections();
    return currentSectionIndex >= sections.length - 1;
  };

  const handleSubmit = async () => {
    if (!survey || !publicId) return;

    // Safety check: Only allow submission from the last section
    const sections = getSortedSections();
    if (currentSectionIndex !== sections.length - 1) {
      console.warn("Attempted to submit from non-last section, ignoring");
      return;
    }

    // Validate current section before submitting
    const isValid = await validateCurrentSection();
    if (!isValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Waits for the background challenge to finish if it hasn't yet
      if (!turnstileRef.current) {
        throw new TurnstileTokenError("Turnstile widget not mounted");
      }
      const turnstileToken = await turnstileRef.current.getToken();

      // Get ALL form values including from unmounted fields (previous sections)
      const allValues = form.getFieldsValue(true);

      // Build answers array from all sections
      const answers: SubmitAnswerRequest[] = [];

      // Helper function to process a question
      const processQuestion = (question: SurveyQuestion) => {
        const answer: SubmitAnswerRequest = {
          questionId: question.id!,
        };

        switch (question.type) {
          case QuestionType.SHORT_TEXT: {
            // Handle multiple answer boxes
            if (question.answerCount && question.answerCount > 1) {
              const answers: string[] = [];
              for (let i = 0; i < question.answerCount; i++) {
                const fieldValue = allValues[`question_${question.id}_${i}`];
                if (fieldValue) {
                  answers.push(fieldValue);
                }
              }
              if (answers.length === 0) return;
              // Store multiple answers as JSON array
              answer.textValue = JSON.stringify(answers);
            } else {
              const fieldName = `question_${question.id}`;
              const value = allValues[fieldName];
              if (value === undefined || value === null || value === "") {
                return;
              }
              answer.textValue = value;
            }
            break;
          }
          case QuestionType.LONG_TEXT: {
            const fieldName = `question_${question.id}`;
            const value = allValues[fieldName];
            if (value === undefined || value === null || value === "") {
              return;
            }
            answer.textValue = value;
            break;
          }
          case QuestionType.DATE: {
            const dateValue = allValues[`question_${question.id}`];
            if (dateValue === undefined || dateValue === null) return;
            answer.dateValue = dateValue ? dayjs(dateValue).unix() : undefined;
            break;
          }
          case QuestionType.MULTIPLE_CHOICE: {
            const selectedValue = allValues[`question_${question.id}`];
            if (selectedValue === undefined || selectedValue === null) return;
            answer.selectedOptionId = selectedValue;
            // Include "Other" text if the selected option is an "Other" option
            const selectedOption = question.options.find(
              (o) => o.id === selectedValue
            );
            if (selectedOption?.isOtherOption) {
              answer.textValue =
                allValues[`question_${question.id}_other`] || "";
            }
            break;
          }
          case QuestionType.MULTI_SELECT: {
            const selectedIds = allValues[`question_${question.id}`];
            if (!selectedIds || selectedIds.length === 0) return;
            answer.selectedOptionIds = selectedIds;
            // Include "Other" text if an "Other" option is selected
            const otherOption = question.options.find((o) => o.isOtherOption);
            if (otherOption && selectedIds?.includes(otherOption.id)) {
              answer.textValue =
                allValues[`question_${question.id}_other`] || "";
            }
            break;
          }
          case QuestionType.RATING: {
            const ratingValue = allValues[`question_${question.id}`];
            if (ratingValue === undefined || ratingValue === null) return;
            answer.ratingValue = ratingValue;
            break;
          }
        }

        answers.push(answer);
      };

      // Process questions from all sections
      if (survey.sections) {
        survey.sections.forEach((section) => {
          section.questions.forEach(processQuestion);
        });
      }

      // Use channel-based submit if channel is provided
      if (channel) {
        await surveyApi.submitByChannel(
          channel,
          publicId,
          { language: selectedLanguage, channel, answers, turnstileToken },
          isTestMode
        );
      } else {
        await surveyApi.submit(
          publicId,
          { language: selectedLanguage, answers, turnstileToken },
          isTestMode
        );
      }

      // Set cookies to prevent resubmission and remember language
      setSurveySubmittedCookie(publicId, channel);
      setSurveyLanguageCookie(publicId, selectedLanguage, channel);

      // Clear progress from localStorage on successful submission
      clearSurveyProgress(publicId, channel);

      setIsSuccess(true);
    } catch (err: any) {
      console.error("Survey submission error:", err);
      // Answers stay in the form (and localStorage), so the user can just retry
      const errorCode: string | undefined = err?.response?.data?.code;
      if (err?.response?.status === 429) {
        setError(t("submit_rate_limited"));
      } else if (
        err instanceof TurnstileTokenError ||
        errorCode?.startsWith("TURNSTILE_")
      ) {
        setError(t("turnstile_failed"));
      } else {
        setError(err?.response?.data?.message || t("error_submitting"));
      }
      // Tokens are single-use: get a fresh one ready for the retry
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return <Spin size="large" />;
  }

  // Error state
  if (error && !survey) {
    return (
      <div
        style={{ boxShadow: "0 0 20px 5px #0000009e" }}
        className="bg-[#1b1c31f2] backdrop-blur-md p-8 rounded-lg text-center max-w-md"
      >
        <h2 className="text-2xl text-white mb-4">Survey Not Available</h2>
        <p className="text-gray-400 mb-6">{error}</p>
      </div>
    );
  }

  // Success state (both for new submission and already submitted)
  if (isSuccess || alreadySubmitted) {
    const customSuccessMessage = survey
      ? getLocalizedText(survey.successMessage, selectedLanguage)
      : "";
    const successButtonLabel = survey
      ? getLocalizedText(survey.successButtonLabel, selectedLanguage)
      : "";
    const successButtonUrl = survey?.successButtonUrl;

    return (
      <div
        style={{ boxShadow: "0 0 20px 5px #0000009e" }}
        className="grow flex flex-col bg-[#1b1c31f2] backdrop-blur-md p-6 md:p-8 rounded-lg min-w-[340px] md:min-w-[570px]"
      >
        <div className="flex flex-col justify-center items-center p-4 min-h-[300px]">
          <img
            src={logoImageUrl}
            alt="Logo"
            className={
              logoImageUrl !== DEFAULT_LOGO_IMAGE
                ? "max-h-40 md:max-h-60 w-auto mb-6 max-w-full"
                : "h-12 md:h-16 mb-6"
            }
          />
          {customSuccessMessage ? (
            <div
              className="success-message-content text-center"
              dangerouslySetInnerHTML={{ __html: customSuccessMessage }}
            />
          ) : (
            <h2
              className="text-2xl md:text-3xl text-center mb-4 text-white"
              dangerouslySetInnerHTML={{ __html: t("success_page_message") }}
            />
          )}
          {successButtonLabel && successButtonUrl && (
            <a
              href={successButtonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 px-8 no-underline min-w-[200px] py-3 bg-[#4550fa] hover:bg-[#3a45e0] text-white text-lg font-semibold rounded-lg transition-colors duration-200 text-center"
            >
              {successButtonLabel}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (!survey) {
    return null;
  }

  return (
    <>
      <div
        style={{ boxShadow: "0 0 20px 5px #0000009e" }}
        className="grow flex flex-col bg-[#1b1c31f2] backdrop-blur-md p-6 md:p-8 rounded-lg min-w-[340px] md:min-w-[570px]"
      >
        {/* Header */}
        <div
          className={clsx("flex flex-col items-center mb-4", {
            "!mb-0": step === 1,
          })}
        >
          <img
            src={logoImageUrl}
            alt="Logo"
            className={
              logoImageUrl !== DEFAULT_LOGO_IMAGE
                ? "max-h-40 md:max-h-60 w-auto mb-4 max-w-full"
                : "h-12 md:h-16 mb-4"
            }
          />
          <h1 className="text-xl md:text-2xl text-center text-white font-semibold">
            {getLocalizedText(survey.title, selectedLanguage)}
          </h1>
        </div>

        {/* Progress Bar - only show when viewing sections */}
        {step === 1 && survey.sections && survey.sections.length > 1 && (
          <div className="">
            <Progress
              percent={
                ((currentSectionIndex + 1) / getSortedSections().length) * 100
              }
              showInfo={false}
              strokeColor="#1890ff"
              trailColor="#2a2b45"
              size="small"
            />
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          preserve={true}
          onValuesChange={() => {
            // Debounced save progress on form value changes
            if (step > 0 && !alreadySubmitted) {
              setTimeout(saveProgress, 500);
            }
          }}
          className="w-full flex flex-col grow"
        >
          {/* Step 0: Language Selection */}
          {step === 0 && (
            <div className="flex flex-col grow">
              <div className="grow">
                {/* Welcome Message - only show if there's content */}
                {getLocalizedText(survey.welcomeMessage, selectedLanguage) && (
                  <div
                    className="mb-6 p-4 bg-[#2a2b45] rounded-lg welcome-message-content"
                    dangerouslySetInnerHTML={{
                      __html:
                        getLocalizedText(
                          survey.welcomeMessage,
                          selectedLanguage
                        ) || "",
                    }}
                  />
                )}

                <Form.Item
                  name="selectedLanguage"
                  label={
                    <span className="text-white text-base">
                      {t("selected_language_label")}
                    </span>
                  }
                >
                  <Select
                    size="large"
                    value={selectedLanguage}
                    onChange={handleLanguageChange}
                    options={LANGUAGES(t)
                      .filter(
                        (lang) =>
                          !survey.supportedLanguages ||
                          survey.supportedLanguages.includes(lang.code)
                      )
                      .map((lang) => ({
                        value: lang.code,
                        label: lang.label,
                      }))}
                    className="w-full"
                  />
                </Form.Item>
              </div>

              <Button
                type="primary"
                htmlType="button"
                size="large"
                block
                onClick={(e) => handleNext(e)}
                className="h-12 text-base font-semibold mt-4"
              >
                {t("form_button_start")}
              </Button>
            </div>
          )}

          {/* Step 1+: Section Questions */}
          {step === 1 && survey.sections && survey.sections.length > 0 && (
            <div className="flex flex-col grow">
              {/* Current Section */}
              {(() => {
                const sections = getSortedSections();
                const currentSection = sections[currentSectionIndex];

                if (!currentSection) return null;

                return (
                  <>
                    {/* Section Title - only show if title has content */}
                    {getLocalizedText(
                      currentSection?.title,
                      selectedLanguage
                    ) && (
                      <div>
                        <h2 className="text-xl text-center mt-4 mb-2 md:text-xl text-white font-bold">
                          {getLocalizedText(
                            currentSection.title,
                            selectedLanguage
                          )}
                        </h2>
                      </div>
                    )}

                    <div className="grow space-y-6">
                      {(() => {
                        let questionStartNumber =
                          getQuestionStartNumber(currentSectionIndex);
                        const sortedQuestions = [
                          ...currentSection.questions,
                        ].sort((a, b) => a.order - b.order);

                        // Track the running question number
                        let runningQuestionNumber = questionStartNumber;

                        return sortedQuestions.map((question) => {
                          // Determine if this question has a condition
                          const hasCondition = isConditionalQuestion(question);

                          // Only assign a number if it's not a conditional question
                          const questionNumber = hasCondition
                            ? null
                            : runningQuestionNumber++;

                          // Wrap in conditional visibility if needed
                          const questionContent = (visible: boolean) =>
                            visible ? (
                              <div
                                key={question.id}
                                className="question-block"
                                ref={(el) => {
                                  if (el) {
                                    questionRefs.current.set(question.id!, el);
                                  }
                                }}
                              >
                                <h3
                                  className={clsx(
                                    "text-white text-base md:text-lg font-medium mb-3",
                                    {
                                      "!mb-0":
                                        question.hintText ||
                                        question.type === QuestionType.RATING ||
                                        (question.type ===
                                          QuestionType.MULTI_SELECT &&
                                          !!question.maxSelections),
                                    }
                                  )}
                                >
                                  {questionNumber !== null && (
                                    <span className="text-gray-400 mr-2">
                                      {questionNumber}.
                                    </span>
                                  )}
                                  {getLocalizedText(
                                    question.title,
                                    selectedLanguage
                                  )}
                                  {question.isRequired && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                </h3>

                                {/* Hint text (displayed before the input for all question types) */}
                                {question.hintText &&
                                  getLocalizedText(
                                    question.hintText,
                                    selectedLanguage
                                  ) && (
                                    <p className="text-gray-400 text-sm mb-3 mt-0">
                                      {getLocalizedText(
                                        question.hintText,
                                        selectedLanguage
                                      )}
                                    </p>
                                  )}

                                {/* Rating hint fallback (only show if no custom hint text) */}
                                {question.type === QuestionType.RATING &&
                                  !getLocalizedText(
                                    question.hintText,
                                    selectedLanguage
                                  ) && (
                                    <p className="text-gray-400 text-sm mb-3 mt-0">
                                      {t("rating_guide_message")}
                                    </p>
                                  )}

                                {/* Short Text Question */}
                                {question.type === QuestionType.SHORT_TEXT && (
                                  <>
                                    {/* Single answer box (default) */}
                                    {(!question.answerCount ||
                                      question.answerCount <= 1) && (
                                      <Form.Item
                                        name={`question_${question.id}`}
                                        rules={[
                                          {
                                            required: question.isRequired,
                                            message: t("form_input_required"),
                                          },
                                        ]}
                                      >
                                        <Input
                                          size="large"
                                          placeholder={t(
                                            "form_text_placeholder"
                                          )}
                                          className="bg-[#2a2b45] border-gray-600 text-white"
                                        />
                                      </Form.Item>
                                    )}
                                    {/* Multiple answer boxes */}
                                    {question.answerCount &&
                                      question.answerCount > 1 && (
                                        <div className="space-y-3">
                                          {Array.from({
                                            length: question.answerCount,
                                          }).map((_, index) => (
                                            <Form.Item
                                              key={index}
                                              name={`question_${question.id}_${index}`}
                                              className="!mb-0"
                                              rules={[
                                                {
                                                  required: question.isRequired,
                                                  message: t(
                                                    "form_input_required"
                                                  ),
                                                },
                                              ]}
                                            >
                                              <Input
                                                size="large"
                                                placeholder={`${t(
                                                  "form_text_placeholder"
                                                )} ${index + 1}`}
                                                className="bg-[#2a2b45] border-gray-600 text-white"
                                              />
                                            </Form.Item>
                                          ))}
                                        </div>
                                      )}
                                  </>
                                )}

                                {/* Long Text Question */}
                                {question.type === QuestionType.LONG_TEXT && (
                                  <Form.Item
                                    name={`question_${question.id}`}
                                    rules={[
                                      {
                                        required: question.isRequired,
                                        message: t("form_input_required"),
                                      },
                                    ]}
                                  >
                                    <Input.TextArea
                                      size="large"
                                      rows={2}
                                      placeholder={t("form_text_placeholder")}
                                      className="bg-[#2a2b45] border-gray-600 text-white"
                                    />
                                  </Form.Item>
                                )}

                                {/* Date Question */}
                                {question.type === QuestionType.DATE && (
                                  <Form.Item
                                    name={`question_${question.id}`}
                                    rules={[
                                      {
                                        required: question.isRequired,
                                        message: t("form_input_required"),
                                      },
                                    ]}
                                  >
                                    <DatePicker
                                      size="large"
                                      className="w-full bg-[#2a2b45] border-gray-600"
                                      format="YYYY-MM-DD"
                                      placeholder={t("form_date_placeholder")}
                                    />
                                  </Form.Item>
                                )}

                                {/* Multiple Choice (Radio) */}
                                {question.type ===
                                  QuestionType.MULTIPLE_CHOICE && (
                                  <>
                                    <Form.Item
                                      name={`question_${question.id}`}
                                      rules={[
                                        {
                                          required: question.isRequired,
                                          message: t("form_input_required"),
                                        },
                                      ]}
                                    >
                                      <Radio.Group className="w-full">
                                        <div className="flex flex-col gap-3">
                                          {question.options
                                            .sort((a, b) => a.order - b.order)
                                            .map((option) => (
                                              <Radio
                                                key={option.id}
                                                value={option.id}
                                                className="text-white survey-radio"
                                              >
                                                {getLocalizedText(
                                                  option.text,
                                                  selectedLanguage
                                                )}
                                              </Radio>
                                            ))}
                                        </div>
                                      </Radio.Group>
                                    </Form.Item>
                                    {/* Text input for "Other" option */}
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, curr) =>
                                        prev[`question_${question.id}`] !==
                                        curr[`question_${question.id}`]
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const selectedOptionId = getFieldValue(
                                          `question_${question.id}`
                                        );
                                        const selectedOption =
                                          question.options.find(
                                            (o) => o.id === selectedOptionId
                                          );
                                        if (!selectedOption?.isOtherOption)
                                          return null;
                                        return (
                                          <Form.Item
                                            name={`question_${question.id}_other`}
                                            rules={[
                                              {
                                                required: question.isRequired,
                                                message: t(
                                                  "form_input_required"
                                                ),
                                              },
                                            ]}
                                            className="-mt-3"
                                          >
                                            <Input
                                              size="large"
                                              placeholder={t(
                                                "other_placeholder"
                                              )}
                                              className="bg-[#2a2b45] border-gray-600 text-white"
                                            />
                                          </Form.Item>
                                        );
                                      }}
                                    </Form.Item>
                                  </>
                                )}

                                {/* Multi Select (Checkbox) */}
                                {question.type ===
                                  QuestionType.MULTI_SELECT && (
                                  <>
                                    {!!question.maxSelections && (
                                      <p className="text-gray-400 text-sm mb-3 mt-0">
                                        {t("multi_select_max_hint", {
                                          count: question.maxSelections,
                                        })}
                                      </p>
                                    )}
                                    {/* Re-render on change so unchecked options lock once the limit is reached */}
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, curr) =>
                                        prev[`question_${question.id}`] !==
                                        curr[`question_${question.id}`]
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const maxSelections =
                                          question.maxSelections;
                                        const selectedOptionIds: number[] =
                                          getFieldValue(
                                            `question_${question.id}`
                                          ) || [];
                                        const limitReached =
                                          !!maxSelections &&
                                          selectedOptionIds.length >=
                                            maxSelections;
                                        return (
                                          <Form.Item
                                            name={`question_${question.id}`}
                                            rules={[
                                              {
                                                required: question.isRequired,
                                                message: t(
                                                  "form_input_required"
                                                ),
                                              },
                                              {
                                                validator: (_, value) =>
                                                  !maxSelections ||
                                                  !Array.isArray(value) ||
                                                  value.length <=
                                                    maxSelections
                                                    ? Promise.resolve()
                                                    : Promise.reject(
                                                        new Error(
                                                          t(
                                                            "multi_select_max_hint",
                                                            {
                                                              count:
                                                                maxSelections,
                                                            }
                                                          )
                                                        )
                                                      ),
                                              },
                                            ]}
                                          >
                                            <Checkbox.Group className="w-full">
                                              <div className="flex flex-col gap-3">
                                                {question.options
                                                  .sort(
                                                    (a, b) => a.order - b.order
                                                  )
                                                  .map((option) => (
                                                    <Checkbox
                                                      key={option.id}
                                                      value={option.id}
                                                      disabled={
                                                        limitReached &&
                                                        !selectedOptionIds.includes(
                                                          option.id!
                                                        )
                                                      }
                                                      className="text-white survey-checkbox"
                                                    >
                                                      {getLocalizedText(
                                                        option.text,
                                                        selectedLanguage
                                                      )}
                                                    </Checkbox>
                                                  ))}
                                              </div>
                                            </Checkbox.Group>
                                          </Form.Item>
                                        );
                                      }}
                                    </Form.Item>
                                    {/* Text input for "Other" option */}
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, curr) =>
                                        prev[`question_${question.id}`] !==
                                        curr[`question_${question.id}`]
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const selectedOptionIds: number[] =
                                          getFieldValue(
                                            `question_${question.id}`
                                          ) || [];
                                        const otherOption =
                                          question.options.find(
                                            (o) => o.isOtherOption
                                          );
                                        if (
                                          !otherOption ||
                                          !selectedOptionIds.includes(
                                            otherOption.id!
                                          )
                                        )
                                          return null;
                                        return (
                                          <Form.Item
                                            name={`question_${question.id}_other`}
                                            rules={[
                                              {
                                                required: question.isRequired,
                                                message: t(
                                                  "form_input_required"
                                                ),
                                              },
                                            ]}
                                            className="-mt-3"
                                          >
                                            <Input
                                              size="large"
                                              placeholder={t(
                                                "other_placeholder"
                                              )}
                                              className="bg-[#2a2b45] border-gray-600 text-white"
                                            />
                                          </Form.Item>
                                        );
                                      }}
                                    </Form.Item>
                                  </>
                                )}

                                {/* Rating (Stars) */}
                                {question.type === QuestionType.RATING && (
                                  <Form.Item
                                    name={`question_${question.id}`}
                                    rules={[
                                      {
                                        required: question.isRequired,
                                        message: t("form_input_required"),
                                      },
                                    ]}
                                  >
                                    <RatingInput />
                                  </Form.Item>
                                )}
                              </div>
                            ) : null;

                          // If question has a condition, wrap in Form.Item with shouldUpdate
                          if (hasCondition) {
                            return (
                              <Form.Item
                                key={question.id}
                                noStyle
                                shouldUpdate={(prevValues, curValues) => {
                                  const conditionField = `question_${question.conditionQuestionId}`;
                                  return (
                                    prevValues[conditionField] !==
                                    curValues[conditionField]
                                  );
                                }}
                              >
                                {() => {
                                  const allValues = form.getFieldsValue(true);
                                  const visible = isQuestionVisible(
                                    question,
                                    allValues
                                  );
                                  return questionContent(visible);
                                }}
                              </Form.Item>
                            );
                          }

                          // No condition, always render
                          return questionContent(true);
                        });
                      })()}

                      {/* Invisible unless Cloudflare needs the visitor to interact */}
                      {isLastSection() && (
                        <TurnstileWidget
                          ref={turnstileRef}
                          siteKey={TURNSTILE_SITE_KEY}
                          action={TURNSTILE_ACTION}
                          language={selectedLanguage}
                          loadErrorMessage={t("turnstile_load_error")}
                          retryLabel={t("turnstile_retry")}
                        />
                      )}

                      {error && (
                        <div className="text-red-500 text-center p-3 bg-red-500/10 rounded">
                          {error}
                        </div>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-col gap-3 mt-4">
                      <div className="flex gap-3">
                        <Button
                          key="back-btn"
                          size="large"
                          htmlType="button"
                          onClick={(e) => handleBack(e)}
                          className="h-12 text-base font-semibold flex-1"
                        >
                          {t("form_button_back")}
                        </Button>
                        {isLastSection() ? (
                          <Button
                            key="submit-btn"
                            type="primary"
                            htmlType="submit"
                            size="large"
                            disabled={isSubmitting}
                            className="h-12 text-base font-semibold flex-1"
                          >
                            {isSubmitting ? (
                              <Spin size="small" />
                            ) : (
                              t("form_button_submit")
                            )}
                          </Button>
                        ) : (
                          <Button
                            key="next-btn"
                            type="primary"
                            htmlType="button"
                            size="large"
                            onClick={(e) => handleNext(e)}
                            className="h-12 text-base font-semibold flex-1"
                          >
                            {t("form_button_next")}
                          </Button>
                        )}
                      </div>
                      {/* Review Answers button - only show on last section when there are multiple sections */}
                      {isLastSection() && getSortedSections().length > 1 && (
                        <Button
                          key="review-btn"
                          size="large"
                          htmlType="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setReviewModalOpen(true);
                          }}
                          className="h-12 text-base font-semibold w-full"
                          icon={<CheckCircleOutlined />}
                        >
                          {t("form_button_review")}
                        </Button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </Form>

        {/* Review Answers Modal */}
        {survey && (
          <ReviewAnswersModal
            open={reviewModalOpen}
            onClose={() => setReviewModalOpen(false)}
            onNavigateToQuestion={handleNavigateToQuestion}
            survey={survey}
            formValues={form.getFieldsValue(true)}
            selectedLanguage={selectedLanguage}
            t={t}
          />
        )}

        {/* Disclaimer */}
        <div className="mt-auto pt-6 border-t border-gray-700">
          <p className="text-gray-400 text-xs text-center leading-relaxed">
            {t("survey_disclaimer")}
          </p>
        </div>
      </div>
      {/* Progress Restored Notification */}
      {progressRestored && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in-up">
          <CheckCircleOutlined />
          <span>{t("progress_restored")}</span>
        </div>
      )}
    </>
  );
};
